import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { PixelRatio, StyleSheet } from "react-native";
import type { LayoutChangeEvent, LayoutRectangle } from "react-native";
import Animated, {
  cancelAnimation,
  Easing,
  interpolate,
  interpolateColor,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import type { SharedValue } from "react-native-reanimated";
import { scheduleOnRN } from "react-native-worklets";

import { CODE_LENGTH } from "./CodeStep";
import type { CodeStepGeometry, CodeStepMotion } from "./CodeStep";

/** The glide Link in Bio's sheet makes (fieldBecomesCode in its contact
 *  step): the stand-ins and the sheet's height travel on the same beat. */
const GLIDE_MS = 420;
const glide = Easing.bezierFn(0.32, 0.72, 0, 1);
/** The last piece to arrive (the line under the slots) is in by now. */
const ARRIVAL_MS = 660;
const CLAMP = "clamp";

/** How the email field the step grows out of is drawn. */
export type FieldLook = {
  surface: string;
  border: string;
  /** The border's colour at no opacity: the seams between the slices. */
  borderClear: string;
  radius: number;
  /** The typed address: its inset in the field, and how it is set. */
  textInset: number;
  textColor: string;
  textSize: number;
  textLine: number;
  textFont: string;
  /** What the address becomes in the chip. */
  chipTextSize: number;
  slotRadius: number;
  slotGap: number;
  button: string;
};

type From = { field: LayoutRectangle; button: LayoutRectangle };

/**
 * The email field becomes the code field, as on Link in Bio: its outline
 * divides into the six slots, the typed address lifts into its chip, the
 * button drains away and the words around them fade in, while the box holding
 * both glides to the code step's height.
 *
 * Both steps stay mounted, so the keyboard is handed from one field to the
 * other. The email pane lays the box out until the code step has landed,
 * then the code step does; the one not laying it out sits over it. Stand-ins
 * drawn over the box make the move, and the real slots and address take over
 * as they land.
 *
 * `codeUp`: a code is on its way to an address. Going back to the address
 * is not drawn.
 */
export function useFieldBecomesCode(codeUp: boolean) {
  const reducedMotion = useReducedMotion();
  /** Milliseconds into the arrival. */
  const clock = useSharedValue(0);
  /** The box's height while it glides; -1 leaves it to its content. */
  const height = useSharedValue(-1);
  /** The email field and its button have gone into the stand-ins. */
  const away = useSharedValue(0);
  const shape = useRef<{ pane?: number } & Partial<From>>({});
  const started = useRef(false);
  const [from, setFrom] = useState<From | null>(null);
  const [to, setTo] = useState<CodeStepGeometry | null>(null);
  const [drawing, setDrawing] = useState(false);
  const [flow, setFlow] = useState<"email" | "code">("email");

  // Back at the address: nothing of the arrival stays.
  const [wasCodeUp, setWasCodeUp] = useState(codeUp);
  if (wasCodeUp !== codeUp) {
    setWasCodeUp(codeUp);
    if (!codeUp) {
      setTo(null);
      setDrawing(false);
      setFlow("email");
    }
  }
  useEffect(() => {
    if (codeUp) return;
    cancelAnimation(clock);
    cancelAnimation(height);
    clock.set(0);
    height.set(-1);
    away.set(0);
    started.current = false;
  }, [codeUp, clock, height, away]);

  /** The code step is laid out: the move starts from the field as it is. */
  const arrive = (geometry: CodeStepGeometry) => {
    if (!codeUp || started.current) return;
    started.current = true;
    const { pane, field, button } = shape.current;
    away.set(1);
    if (reducedMotion || !pane || !field || !button) {
      clock.set(ARRIVAL_MS);
      setFlow("code");
      return;
    }
    const landed = () => {
      setFlow("code");
      setDrawing(false);
    };
    setFrom({ field, button });
    setTo(geometry);
    setDrawing(true);
    height.set(pane);
    height.set(
      withTiming(geometry.height, {
        duration: GLIDE_MS,
        easing: Easing.bezier(0.32, 0.72, 0, 1),
      }),
    );
    clock.set(
      withTiming(
        ARRIVAL_MS,
        { duration: ARRIVAL_MS, easing: Easing.linear },
        (finished) => {
          if (finished) scheduleOnRN(landed);
        },
      ),
    );
  };

  // The code step lays the box out from here: its height is its content's
  // again (an error line may come and go).
  useLayoutEffect(() => {
    if (flow === "code") height.set(-1);
  }, [flow, height]);

  const boxStyle = useAnimatedStyle(() => ({
    height: height.get() < 0 ? "auto" : height.get(),
  }));
  /** The field and its button: gone the moment the stand-ins draw them. */
  const fieldStyle = useAnimatedStyle(() => ({
    opacity: away.get() ? 0 : 1,
  }));
  /** The words around them fade as the step leaves. */
  const wordsStyle = useAnimatedStyle(() => ({
    opacity: away.get()
      ? interpolate(clock.get(), [0, 120], [1, 0], CLAMP)
      : 1,
  }));

  const motion: CodeStepMotion = {
    label: useAnimatedStyle(() => ({
      opacity: interpolate(clock.get(), [120, 360], [0, 1], CLAMP),
    })),
    chip: useAnimatedStyle(() => ({
      opacity: interpolate(clock.get(), [200, 400], [0, 1], CLAMP),
    })),
    // The chip's own address waits for the flying one to land on it, and the
    // drawn slots for the stand-ins.
    address: useAnimatedStyle(() => ({
      opacity: clock.get() >= GLIDE_MS ? 1 : 0,
    })),
    slots: useAnimatedStyle(() => ({
      opacity: clock.get() >= GLIDE_MS ? 1 : 0,
    })),
    status: useAnimatedStyle(() => {
      const t = interpolate(clock.get(), [300, ARRIVAL_MS], [0, 1], CLAMP);
      return {
        opacity: t,
        transform: [{ translateY: 6 * (1 - glide(t)) }],
      };
    }),
  };

  return {
    /** Which step lays the box out; the other sits over it, at its top. */
    flow,
    boxStyle,
    fieldStyle,
    wordsStyle,
    motion,
    clock,
    /** The stand-ins' ends, while they are drawn. */
    drawn: drawing && from && to ? { from, to } : null,
    onEmailPaneLayout: (event: LayoutChangeEvent) => {
      // Only its own height: sat over the code step it is not measured.
      if (flow === "email") shape.current.pane = event.nativeEvent.layout.height;
    },
    onFieldLayout: (event: LayoutChangeEvent) => {
      shape.current.field = event.nativeEvent.layout;
    },
    onButtonLayout: (event: LayoutChangeEvent) => {
      shape.current.button = event.nativeEvent.layout;
    },
    onGeometry: arrive,
  };
}

/** Over the box, at the top of both steps. */
export const OVER_BOX = {
  left: 0,
  position: "absolute",
  right: 0,
  top: 0,
} as const;

/**
 * The stand-ins: the field's outline as six slices travelling to the slots,
 * the typed address travelling to its chip, and the button draining away.
 */
export function FieldBecomesCode({
  clock,
  from,
  to,
  look,
  address,
}: {
  clock: SharedValue<number>;
  from: From;
  to: CodeStepGeometry;
  look: FieldLook;
  address: string;
}) {
  const textTop = from.field.y + (from.field.height - look.textLine) / 2;
  const textLeft = from.field.x + look.textInset;
  const scale = look.chipTextSize / look.textSize;

  const flying = useAnimatedStyle(() => {
    const p = glide(Math.min(1, clock.get() / GLIDE_MS));
    return {
      opacity: clock.get() < GLIDE_MS ? 1 : 0,
      transform: [
        { translateX: (to.address.x - textLeft) * p },
        {
          translateY:
            (to.address.y +
              to.address.height / 2 -
              (look.textLine * scale) / 2 -
              textTop) *
            p,
        },
        { scale: 1 + (scale - 1) * p },
      ],
    };
  });
  const button = useAnimatedStyle(() => ({
    opacity: interpolate(clock.get(), [0, 120], [0.45, 0], CLAMP),
    // It goes where the outline goes, so it fades inside it.
    top:
      from.button.y +
      (to.slots.y - from.field.y) *
        glide(Math.min(1, clock.get() / GLIDE_MS)),
    transform: [
      { scale: interpolate(clock.get(), [0, 120], [1, 0.8], CLAMP) },
    ],
  }));

  return (
    <Animated.View
      aria-hidden
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      pointerEvents="none"
      style={StyleSheet.absoluteFill}
    >
      {Array.from({ length: CODE_LENGTH }, (_, index) => (
        <Slice
          clock={clock}
          from={from.field}
          index={index}
          key={index}
          look={look}
          to={to.slots}
        />
      ))}
      <Animated.Text
        numberOfLines={1}
        style={[
          {
            color: look.textColor,
            fontFamily: look.textFont,
            fontSize: look.textSize,
            left: textLeft,
            lineHeight: look.textLine,
            maxWidth: from.field.width - look.textInset * 2,
            position: "absolute",
            top: textTop,
            transformOrigin: "left top",
          },
          flying,
        ]}
      >
        {address}
      </Animated.Text>
      <Animated.View
        style={[
          {
            backgroundColor: look.button,
            borderRadius: from.button.height / 2,
            height: from.button.height,
            left: from.button.x,
            position: "absolute",
            width: from.button.width,
          },
          button,
        ]}
      />
    </Animated.View>
  );
}

/** One slice of the field's outline: square where it meets its neighbours
 *  and with no line between them, so the six start as one box. */
function Slice({
  clock,
  from,
  to,
  index,
  look,
}: {
  clock: SharedValue<number>;
  from: LayoutRectangle;
  /** The row of slots. */
  to: LayoutRectangle;
  index: number;
  look: FieldLook;
}) {
  const first = index === 0;
  const last = index === CODE_LENGTH - 1;
  // On the pixel grid, so the slices meet without a hairline between them.
  const edge = (n: number) =>
    PixelRatio.roundToNearestPixel(from.x + (from.width * n) / CODE_LENGTH);
  const left = edge(index);
  const width = edge(index + 1) - left;
  const slot = (to.width - look.slotGap * (CODE_LENGTH - 1)) / CODE_LENGTH;
  const slotLeft = to.x + index * (slot + look.slotGap);

  const style = useAnimatedStyle(() => {
    const p = glide(Math.min(1, clock.get() / GLIDE_MS));
    const between = (a: number, b: number) => a + (b - a) * p;
    const start = between(first ? look.radius : 0, look.slotRadius);
    const end = between(last ? look.radius : 0, look.slotRadius);
    const seam = interpolateColor(
      p,
      [0, 1],
      [look.borderClear, look.border],
    );
    return {
      borderBottomLeftRadius: start,
      borderBottomRightRadius: end,
      borderLeftColor: first ? look.border : seam,
      borderRightColor: last ? look.border : seam,
      borderTopLeftRadius: start,
      borderTopRightRadius: end,
      height: between(from.height, to.height),
      left: between(left, slotLeft),
      opacity: clock.get() < GLIDE_MS ? 1 : 0,
      top: between(from.y, to.y),
      width: between(width, slot),
    };
  });

  return (
    <Animated.View
      style={[
        {
          backgroundColor: look.surface,
          borderColor: look.border,
          borderWidth: 1,
          position: "absolute",
        },
        style,
      ]}
    />
  );
}
